// src/components/Header.tsx
import React, { useState, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import SlideMenuPanel from "components/menus/SlideMenuPanel";
import { useMe } from "contexts/MeContext";

type Props = {
  onMenuClick?: () => void;
  /** LP側などから渡す実データ。将来用に残すが、基本は MeContext を使う */
  userId?: string;
  userName?: string;
  points?: number;
};

const LINE_FRIEND_URL = "https://lin.ee/EDKV4tY";

// 新ヘッダー素材
const HEADER_BG = "/assets/member/new-haikei.webp";
const HEADER_LOGO = "/assets/member/header-logo.webp";

// ヘッダーリンク（濃紺）
const HEADER_LINKS = [
  { to: "/", alt: "HOME", img: "header-home.webp" },
  { to: "/plans", alt: "プラン一覧", img: "header-plan.webp" },
  { to: "/mypage", alt: "マイページ", img: "header-mypage.webp" },
  { to: "/points-buy", alt: "ポイント購入", img: "header-point.webp" },
] as const;

const Header: React.FC<Props> = ({ onMenuClick, userId, userName, points }) => {
  const [open, setOpen] = useState(false);
  const { me } = useMe();

  // MeContext（/api/auth/me）＋ props から会員情報を決定
  const anyMe = me as any;

  const rawUserId: string | undefined =
    userId ??
    (me?.id != null
      ? String(me.id)
      : anyMe?.userId != null
      ? String(anyMe.userId)
      : undefined);

  const rawUserName: string | undefined =
    (userName ?? (me?.lineName || anyMe?.name || "")).trim() || undefined;

  const rawPoints: number | string | undefined =
    typeof points !== "undefined"
      ? points
      : typeof me?.points !== "undefined"
      ? me.points
      : anyMe?.purchasePoint;

  const toggle = useCallback(
    (e?: React.MouseEvent) => {
      e?.stopPropagation();
      setOpen((v) => !v);
      onMenuClick?.();
    },
    [onMenuClick]
  );

  const close = useCallback(() => setOpen(false), []);

  // メニュー開時に背景スクロールを止める（iOS含む）
  useEffect(() => {
    const prev = document.body.style.overflow;
    if (open) document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // ★ 追加：メニュー開閉に合わせて body にクラス付与（Footer 側 pointer-events 無効化に使う）
  useEffect(() => {
    if (open) document.body.classList.add("menuOpen");
    else document.body.classList.remove("menuOpen");
    return () => document.body.classList.remove("menuOpen");
  }, [open]);

  // 表示用整形
  const displayUserId =
    rawUserId && /^\d+$/.test(rawUserId) ? rawUserId : "—";

  const displayPoints =
    typeof rawPoints === "number"
      ? rawPoints.toLocaleString()
      : typeof rawPoints === "string" && rawPoints !== ""
      ? Number(rawPoints).toLocaleString()
      : "—";

  // gap（既存維持）
  const [isMobile, setIsMobile] = useState<boolean>(() =>
    typeof window !== "undefined" ? window.innerWidth <= 768 : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize, { passive: true });
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // ログイン判定
  const isLoggedIn = !!rawUserId;

  // 友だち追加ボタン表示条件：
  // - ログイン済み
  // - friendBonusClaimed が true でない（undefined のときは表示）
  const friendBonusClaimed =
    typeof anyMe?.friendBonusClaimed === "boolean"
      ? anyMe.friendBonusClaimed
      : false;

  const showFriendBonusButton = isLoggedIn && !friendBonusClaimed;

  return (
    <>
      <header
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 720,
          margin: "0 auto",
          backgroundImage: `url(${HEADER_BG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          backgroundColor: "transparent",
          padding: "10px 10px 0",
          position: "sticky",
          top: 0,

          // ★ open中は header を下げる（保険：メニューに被らせない）
          zIndex: open ? 10 : 1000,

          pointerEvents: "auto",
          overflow: "hidden",
        }}
      >
        {/* 背景オーバーレイ（薄く） */}
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(180deg, rgba(255,255,255,0.15), rgba(255,255,255,0.05))",
            pointerEvents: "none",
            zIndex: 0,
          }}
        />

        {/* === 上段：ロゴ／メニュー === */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "auto 1fr auto",
            alignItems: "center",
            columnGap: 10,
            position: "relative",
            zIndex: 1,
          }}
        >
          {/* 左上ロゴ（新デザイン） */}
          <Link
            to="/"
            aria-label="HOME"
            onClick={(e) => e.stopPropagation()}
            style={{
              display: "inline-block",
              position: "relative",
              zIndex: 1,
            }}
          >
            <img
              src={HEADER_LOGO}
              alt="BOAT OKUMAN"
              style={{ height: 58, width: "auto", display: "block" }}
              loading="eager"
            />
          </Link>

          <div />

          {/* ハンバーガー（濃紺） */}
          <button
            onClick={toggle}
            aria-label="メニューを開く"
            style={{
              width: 60,
              height: 56,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 10px",
              background: "transparent",
              border: "none",
              cursor: "pointer",
              position: "relative",
              zIndex: 2,
              WebkitTapHighlightColor: "transparent",
            }}
          >
            <span
              style={{
                width: 40,
                height: 6,
                background: "#0e1b26",
                borderRadius: 4,
              }}
            />
            <span
              style={{
                width: 40,
                height: 6,
                background: "#0e1b26",
                borderRadius: 4,
              }}
            />
            <span
              style={{
                width: 40,
                height: 6,
                background: "#0e1b26",
                borderRadius: 4,
              }}
            />
          </button>
        </div>

        {/* === 会員情報（ロゴ＆ハンバーガーの下／プレート無し） === */}
        <div
          aria-label="会員情報"
          style={{
            marginTop: 10,
            padding: "0 4px",
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            columnGap: 12,
            alignItems: "end",
            color: "#0e1b26",
            fontWeight: 800,
            textShadow: "0 1px 0 rgba(255,255,255,0.55)",
            position: "relative",
            zIndex: 1,
          }}
        >
          {/* USERNAME（中央揃え） */}
          <div style={{ minWidth: 0, textAlign: "center" }}>
            <div style={{ fontSize: 12, opacity: 0.85 }}>USERNAME</div>
            <div
              style={{
                marginTop: 2,
                fontSize: 13,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                marginLeft: "auto",
                marginRight: "auto",
                maxWidth: "100%",
              }}
            >
              {rawUserName || "—"}
            </div>
            <div
              style={{
                marginTop: 4,
                height: 2,
                background: "rgba(14,27,38,0.45)",
                borderRadius: 2,
                width: "72%",
                marginLeft: "auto",
                marginRight: "auto",
              }}
            />
          </div>

          {/* 会員ID（中央揃え） */}
          <div style={{ minWidth: 0, textAlign: "center" }}>
            <div style={{ fontSize: 12, opacity: 0.85 }}>会員ID</div>
            <div style={{ marginTop: 2, fontSize: 13 }}>{displayUserId}</div>
            <div
              style={{
                marginTop: 4,
                height: 2,
                background: "rgba(14,27,38,0.45)",
                borderRadius: 2,
                width: "72%",
                marginLeft: "auto",
                marginRight: "auto",
              }}
            />
          </div>

          {/* 保有PT（中央揃え） */}
          <div style={{ minWidth: 0, textAlign: "center" }}>
            <div style={{ fontSize: 12, opacity: 0.85 }}>保有PT</div>
            <div style={{ marginTop: 2, fontSize: 13 }}>
              {displayPoints}
              <span style={{ marginLeft: 4, fontSize: 12, fontWeight: 900 }}>
                pt
              </span>
            </div>
            <div
              style={{
                marginTop: 4,
                height: 2,
                background: "rgba(14,27,38,0.45)",
                borderRadius: 2,
                width: "72%",
                marginLeft: "auto",
                marginRight: "auto",
              }}
            />
          </div>
        </div>

        {/* === 下段：メニューボタン（新4リンク） === */}
        <nav
          aria-label="ヘッダーリンク"
          onClick={(e) => e.stopPropagation()}
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: isMobile ? 6 : 10,
            margin: "10px 0 8px",
            padding: "0 2px",
            position: "relative",
            zIndex: 1,
            pointerEvents: "auto",
          }}
        >
          {HEADER_LINKS.map(({ to, alt, img }) => (
            <Link key={to} to={to} aria-label={alt} style={{ display: "block" }}>
              <img
                src={`/assets/member/${img}`}
                alt={alt}
                style={{ width: "100%", display: "block" }}
                loading="lazy"
              />
            </Link>
          ))}
        </nav>

        {/* === 友だち追加で50pt GET（薄いゴールドグラデーション） === */}
        {showFriendBonusButton && (
          <div
            style={{
              padding: "0 4px 8px",
              display: "flex",
              justifyContent: "center",
              position: "relative",
              zIndex: 1,
            }}
          >
            <a
              href={LINE_FRIEND_URL}
              target="_blank"
              rel="noreferrer"
              style={{
                display: "inline-block",
                width: "100%",
                maxWidth: 720,
                textDecoration: "none",
              }}
            >
              <button
                type="button"
                style={{
                  width: "100%",
                  borderRadius: 999,
                  padding: "10px 12px",
                  fontSize: 16,
                  fontWeight: 900,
                  letterSpacing: "0.06em",
                  cursor: "pointer",
                  background:
                    "linear-gradient(90deg, #fdf6dc 0%, #f6e7b2 40%, #edd58a 60%, #f6e7b2 100%)",
                  border: "1px solid #e6c96b",
                  color: "#5a3b00",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
                  textShadow: "0 1px 1px rgba(255,255,255,0.6)",
                  WebkitTapHighlightColor: "transparent",
                }}
              >
                🎁 友だち追加で50pt GET
              </button>
            </a>
          </div>
        )}
      </header>

      {/* =========================
          ★ 重要：スライドメニューを “最前面固定レイヤー” に強制
          - これで footerDock / headerボタンの上に必ず乗る
         ========================= */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 20000, // ← ここで勝つ
          pointerEvents: open ? "auto" : "none",
        }}
        aria-hidden={!open}
      >
        <SlideMenuPanel open={open} onClose={close} />
      </div>
    </>
  );
};

export default Header;